import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { XMarkIcon, UserIcon, KeyIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { FormField, Input, Select } from '../../components/Forms';
import { Permission, User } from '../../types';
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
  clonePermissions,
  getDefaultPermissionsForRole,
  hasAction,
  togglePermissionAction,
} from '../../utils/userPermissions';

export type UserFormMode = 'create' | 'edit' | 'view';

interface UserFormProps {
  user?: User | null;
  mode: UserFormMode;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (user: User) => void;
}

interface UserFormState {
  username: string;
  fullName: string;
  phone: string;
  email: string;
  role: User['role'] | '';
  password: string;
  confirmPassword: string;
  permissions: Permission[];
}

const ROLE_OPTIONS = [
  { value: 'operations', label: 'عمليات' },
  { value: 'hotel_rep', label: 'مندوب فندق' },
  { value: 'accounting', label: 'محاسبة' },
  { value: 'admin', label: 'مدير النظام' },
];

const emptyForm: UserFormState = {
  username: '',
  fullName: '',
  phone: '',
  email: '',
  role: 'operations',
  password: '',
  confirmPassword: '',
  permissions: getDefaultPermissionsForRole('operations'),
};

const UserForm: React.FC<UserFormProps> = ({ user, mode, isOpen, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<UserFormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const isView = mode === 'view';
  const isCreate = mode === 'create';

  useEffect(() => {
    if (!isOpen) return;
    setSubmitError('');
    setErrors({});
    if (user && (mode === 'edit' || mode === 'view')) {
      setForm({
        username: user.username || '',
        fullName: user.fullName || '',
        phone: user.phone || '',
        email: user.email || '',
        role: user.role || 'operations',
        password: '',
        confirmPassword: '',
        permissions:
          user.permissions?.length > 0
            ? clonePermissions(user.permissions)
            : getDefaultPermissionsForRole(user.role || 'operations'),
      });
    } else {
      setForm({
        ...emptyForm,
        permissions: getDefaultPermissionsForRole('operations'),
      });
    }
  }, [isOpen, user, mode]);

  const createMutation = useMutation({
    mutationFn: (payload: Partial<User> & { password: string }) =>
      apiService.createUser(payload),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onSuccess?.(created);
      onClose();
    },
    onError: (err: any) => {
      setSubmitError(err?.message || 'فشل إنشاء المستخدم');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Partial<User>) => apiService.updateUser(user!._id, payload),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onSuccess?.(updated);
      onClose();
    },
    onError: (err: any) => {
      setSubmitError(err?.message || 'فشل تحديث المستخدم');
    },
  });

  const setField = (field: keyof UserFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRoleChange = (role: string) => {
    setForm((prev) => ({
      ...prev,
      role: role as User['role'] | '',
      permissions: role ? getDefaultPermissionsForRole(role) : [],
    }));
  };

  const handleToggleAction = (
    module: Permission['module'],
    action: Permission['actions'][number],
    enabled: boolean
  ) => {
    setForm((prev) => ({
      ...prev,
      permissions: togglePermissionAction(prev.permissions, module, action, enabled),
    }));
  };

  const applyRoleDefaults = () => {
    if (!form.role) return;
    setForm((prev) => ({
      ...prev,
      permissions: getDefaultPermissionsForRole(prev.role),
    }));
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.username.trim() || form.username.trim().length < 3) {
      next.username = 'اسم المستخدم يجب أن يكون 3 أحرف على الأقل';
    } else if (!/^[a-zA-Z0-9_]+$/.test(form.username.trim())) {
      next.username = 'اسم المستخدم: أحرف إنجليزية وأرقام وشرطة سفلية فقط';
    }
    if (!form.fullName.trim() || form.fullName.trim().length < 2) {
      next.fullName = 'الاسم الكامل مطلوب';
    }
    if (!form.role) {
      next.role = 'الدور مطلوب';
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = 'البريد الإلكتروني غير صحيح';
    }
    if (!form.permissions.some((p) => (p.actions || []).length > 0)) {
      next.permissions = 'حدد صلاحية واحدة على الأقل';
    }
    if (isCreate) {
      if (!form.password || form.password.length < 6) {
        next.password = 'كلمة المرور يجب أن تكون 6 أحرف على الأقل';
      }
      if (form.password !== form.confirmPassword) {
        next.confirmPassword = 'كلمتا المرور غير متطابقتين';
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isView) return;
    setSubmitError('');
    if (!validate()) return;

    const permissions = form.permissions.filter((p) => (p.actions || []).length > 0);

    if (isCreate) {
      createMutation.mutate({
        username: form.username.trim(),
        fullName: form.fullName.trim(),
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        role: form.role as User['role'],
        password: form.password,
        permissions,
      });
      return;
    }

    updateMutation.mutate({
      username: form.username.trim(),
      fullName: form.fullName.trim(),
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || undefined,
      role: form.role as User['role'],
      permissions,
    });
  };

  if (!isOpen) return null;

  const pending = createMutation.isPending || updateMutation.isPending;
  const title =
    mode === 'create' ? 'إضافة مستخدم جديد' : mode === 'edit' ? 'تعديل المستخدم' : 'تفاصيل المستخدم';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />
        <div className="inline-block w-full max-w-3xl p-6 my-8 overflow-hidden text-right align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <UserIcon className="w-6 h-6 text-primary-600" />
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField label="اسم المستخدم" required error={errors.username}>
              <Input
                value={form.username}
                onChange={(e) => setField('username', e.target.value)}
                disabled={isView || mode === 'edit'}
                placeholder="مثال: bus_staff1"
                error={!!errors.username}
              />
            </FormField>

            <FormField label="الاسم الكامل" required error={errors.fullName}>
              <Input
                value={form.fullName}
                onChange={(e) => setField('fullName', e.target.value)}
                disabled={isView}
                placeholder="الاسم الظاهر في النظام"
                error={!!errors.fullName}
              />
            </FormField>

            <FormField label="الدور" required error={errors.role}>
              <Select
                value={form.role}
                onChange={(e) => handleRoleChange(e.target.value)}
                disabled={isView}
                options={[
                  { value: '', label: 'اختر الدور' },
                  ...ROLE_OPTIONS,
                ]}
              />
            </FormField>

            {form.role === 'operations' && !isView && (
              <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
                لموظف الصعود: فعّل وحدة <strong>بوابة الصعود</strong> (عرض + تعديل). يمكن إيقاف باقي الوحدات.
              </p>
            )}

            {form.role === 'hotel_rep' && !isView && (
              <p className="text-sm text-teal-800 bg-teal-50 border border-teal-200 rounded-lg p-3">
                لمندوب الفندق: وحدة <strong>التسكين</strong> (عرض + تعديل) كافية. أضف رقم الجوال لإرسال نسخة واتساب له.
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="الجوال">
                <Input
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                  disabled={isView}
                  placeholder="05xxxxxxxx"
                />
              </FormField>
              <FormField label="البريد الإلكتروني" error={errors.email}>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  disabled={isView}
                  placeholder="optional@email.com"
                  error={!!errors.email}
                />
              </FormField>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                  <ShieldCheckIcon className="w-5 h-5 text-primary-600" />
                  صلاحيات الوحدات
                </div>
                {!isView && (
                  <button
                    type="button"
                    onClick={applyRoleDefaults}
                    className="text-xs text-primary-700 hover:underline"
                  >
                    استعادة افتراضي الدور
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-white border-b border-gray-100">
                      <th className="text-right px-3 py-2 font-medium text-gray-600">الوحدة</th>
                      {PERMISSION_ACTIONS.map((a) => (
                        <th
                          key={a.action}
                          className="text-center px-2 py-2 font-medium text-gray-600 whitespace-nowrap"
                        >
                          {a.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PERMISSION_MODULES.map((mod) => (
                      <tr key={mod.module} className="border-b border-gray-50 last:border-0">
                        <td className="px-3 py-2 text-right">
                          <div className="font-medium text-gray-900">{mod.label}</div>
                          {mod.hint && (
                            <div className="text-xs text-gray-500 mt-0.5">{mod.hint}</div>
                          )}
                        </td>
                        {PERMISSION_ACTIONS.map((a) => {
                          const checked = hasAction(form.permissions, mod.module, a.action);
                          return (
                            <td key={a.action} className="px-2 py-2 text-center">
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                checked={checked}
                                disabled={isView}
                                onChange={(e) =>
                                  handleToggleAction(mod.module, a.action, e.target.checked)
                                }
                                aria-label={`${mod.label} - ${a.label}`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {errors.permissions && (
                <p className="px-4 py-2 text-sm text-red-600 bg-red-50">{errors.permissions}</p>
              )}
            </div>

            {isCreate && (
              <div className="space-y-4 border-t border-gray-100 pt-4">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <KeyIcon className="w-4 h-4 text-primary-600" />
                  <span>كلمة المرور الأولية — تُعطى للموظف يدوياً بعد الإنشاء</span>
                </div>
                <FormField label="كلمة المرور" required error={errors.password}>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setField('password', e.target.value)}
                    placeholder="6 أحرف على الأقل"
                    error={!!errors.password}
                    autoComplete="new-password"
                  />
                </FormField>
                <FormField label="تأكيد كلمة المرور" required error={errors.confirmPassword}>
                  <Input
                    type="password"
                    value={form.confirmPassword}
                    onChange={(e) => setField('confirmPassword', e.target.value)}
                    placeholder="أعد كتابة كلمة المرور"
                    error={!!errors.confirmPassword}
                    autoComplete="new-password"
                  />
                </FormField>
              </div>
            )}

            {mode === 'edit' && (
              <p className="text-xs text-gray-500 bg-gray-50 rounded-lg p-2">
                لتغيير كلمة المرور استخدم زر «إعادة تعيين كلمة المرور» من قائمة المستخدمين.
              </p>
            )}

            {isView && user && (
              <div className="text-sm text-gray-600 space-y-1 bg-gray-50 rounded-lg p-3">
                <p>
                  الحالة:{' '}
                  <span className={user.isActive ? 'text-green-700' : 'text-red-700'}>
                    {user.isActive ? 'نشط' : 'غير نشط'}
                  </span>
                </p>
                {user.lastLogin && (
                  <p>آخر دخول: {new Date(user.lastLogin).toLocaleString('ar-SA')}</p>
                )}
              </div>
            )}

            {submitError && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                {submitError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="btn-secondary" disabled={pending}>
                {isView ? 'إغلاق' : 'إلغاء'}
              </button>
              {!isView && (
                <button type="submit" className="btn-primary" disabled={pending}>
                  {pending ? 'جاري الحفظ...' : isCreate ? 'إنشاء المستخدم' : 'حفظ التعديلات'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserForm;
